import { getExtensionValue } from "@medplum/core";
import {
  CodeableConcept,
  Practitioner,
  Provenance,
  QuestionnaireResponse,
  Reference,
  Resource,
} from "@medplum/fhirtypes";

import { v5 as uuidv5 } from "uuid";

import {
  EncounterAmendmentReason,
  EncounterAmendmentReasonLabels,
} from "@/encounters/constants/encounter-amendment-reason";
import {
  ENCOUNTER_AMENDMENT_REASON_TO_PROVENANCE_REASON_CODE,
  LABOR_ENCOUNTER_AMEND_COMPLETION_PROVENANCE_ID_NAMESPACE,
  LABOR_ENCOUNTER_PROVENANCE_ACTIVITY_DISPLAY,
  LABOR_ENCOUNTER_PROVENANCE_ACTIVITY_SYSTEM,
  LaborEncounterProvenanceActivityCode,
} from "@/encounters/constants/labor-encounter-provenance";
import { getQuestionnaireResponseEncounterParentVersionId } from "@/encounters/utils/questionnaire-response-amend-stamp";
import { EncounterAmendmentFormType } from "@/encounters/zod-schemas/encounter-answers";
import { Extensions } from "@/fhir/enums/extensions";
import { ProvenanceParticipantType } from "@/fhir/enums/fhir-valuesets";
import { Systems } from "@/fhir/enums/systems";
import { joinReferenceString, setExtensionValue } from "@/utils/medplum";

export interface BuildLaborEncounterEventProvenanceParams {
  activityCode: LaborEncounterProvenanceActivityCode;
  encounterQuestionnaireResponseVersionId: string;
  targets: Reference<Resource>[];
  practitionerReference: Reference<Practitioner>;
  amendmentReason?: EncounterAmendmentReason;
}

/**
 * Returns flowsheet QuestionnaireResponses stamped with the given encounter QuestionnaireResponse version id.
 */
export function filterFlowsheetQuestionnaireResponsesByEncounterParentVersionId(
  questionnaireResponses: Iterable<QuestionnaireResponse>,
  encounterQuestionnaireResponseVersionId: string,
  encounterQuestionnaireResponseId?: string,
): QuestionnaireResponse[] {
  return [...questionnaireResponses].filter((questionnaireResponse) => {
    if (
      encounterQuestionnaireResponseId &&
      questionnaireResponse.id === encounterQuestionnaireResponseId
    ) {
      return false;
    }
    return (
      getQuestionnaireResponseEncounterParentVersionId(questionnaireResponse) ===
      encounterQuestionnaireResponseVersionId
    );
  });
}

/**
 * Builds target references for Encounter, encounter QuestionnaireResponse, and stamped flowsheet QuestionnaireResponses (deduped).
 */
export function buildLaborEncounterEventProvenanceTargets(
  encounterId: string,
  encounterQuestionnaireResponse: QuestionnaireResponse,
  flowsheetQuestionnaireResponses: QuestionnaireResponse[],
): Reference<Resource>[] {
  const targets: Reference<Resource>[] = [
    { reference: joinReferenceString("Encounter", encounterId) },
  ];

  if (encounterQuestionnaireResponse.id) {
    targets.push({
      reference: joinReferenceString("QuestionnaireResponse", encounterQuestionnaireResponse.id),
    });
  }

  const seenReferences = new Set(
    targets.map((target) => target.reference).filter(Boolean) as string[],
  );

  for (const flowsheetQuestionnaireResponse of flowsheetQuestionnaireResponses) {
    if (!flowsheetQuestionnaireResponse.id) {
      continue;
    }
    const reference = joinReferenceString(
      "QuestionnaireResponse",
      flowsheetQuestionnaireResponse.id,
    );
    if (seenReferences.has(reference)) {
      continue;
    }
    seenReferences.add(reference);
    targets.push({ reference });
  }

  return targets;
}

/**
 * Builds the deterministic identifier value for an amend-completion Provenance record.
 */
export function buildLaborEncounterAmendCompletionProvenanceIdentifierValue(
  encounterQuestionnaireResponseId: string,
  encounterQuestionnaireResponseVersionId: string,
): string {
  return `${encounterQuestionnaireResponseId}:${encounterQuestionnaireResponseVersionId}`;
}

/**
 * Builds a stable Provenance resource id for amend-completion records.
 * FHIR R4 Provenance has no `identifier` element; `_id` is used for conditional create.
 */
export function buildLaborEncounterAmendCompletionProvenanceResourceId(
  encounterQuestionnaireResponseId: string,
  encounterQuestionnaireResponseVersionId: string,
): string {
  return uuidv5(
    buildLaborEncounterAmendCompletionProvenanceIdentifierValue(
      encounterQuestionnaireResponseId,
      encounterQuestionnaireResponseVersionId,
    ),
    LABOR_ENCOUNTER_AMEND_COMPLETION_PROVENANCE_ID_NAMESPACE,
  );
}

/**
 * Builds the If-None-Exist query for conditional amend-completion Provenance creation.
 */
export function buildLaborEncounterAmendCompletionProvenanceIfNoneExistQuery(
  encounterQuestionnaireResponseId: string,
  encounterQuestionnaireResponseVersionId: string,
): string {
  const resourceId = buildLaborEncounterAmendCompletionProvenanceResourceId(
    encounterQuestionnaireResponseId,
    encounterQuestionnaireResponseVersionId,
  );
  return `_id=${resourceId}`;
}

/**
 * Returns whether a Provenance records a labor encounter amend-completion event.
 */
export function isLaborEncounterAmendCompletionProvenance(provenance: Provenance): boolean {
  return (
    provenance.activity?.coding?.some(
      (coding) =>
        coding.system === LABOR_ENCOUNTER_PROVENANCE_ACTIVITY_SYSTEM &&
        coding.code === LaborEncounterProvenanceActivityCode.AmendCompletion,
    ) ?? false
  );
}

/**
 * Returns whether an amend-completion Provenance matches the encounter QuestionnaireResponse version.
 */
export function isLaborEncounterAmendCompletionProvenanceForEncounterVersion(
  provenance: Provenance,
  encounterQuestionnaireResponseId: string,
  encounterQuestionnaireResponseVersionId: string,
): boolean {
  if (!isLaborEncounterAmendCompletionProvenance(provenance)) {
    return false;
  }

  if (
    !isLaborEncounterEventProvenanceForVersion(provenance, encounterQuestionnaireResponseVersionId)
  ) {
    return false;
  }

  const encounterQuestionnaireResponseReference = joinReferenceString(
    "QuestionnaireResponse",
    encounterQuestionnaireResponseId,
  );

  return (
    provenance.target?.some(
      (target) => target.reference === encounterQuestionnaireResponseReference,
    ) ?? false
  );
}

/**
 * Finds amend-completion Provenance for an encounter QuestionnaireResponse version within a bundle.
 */
export function findLaborEncounterAmendCompletionProvenanceInBundle(
  provenances: ReadonlyArray<Provenance>,
  encounterQuestionnaireResponseId: string,
  encounterQuestionnaireResponseVersionId: string,
): Provenance | undefined {
  return provenances.find((provenance) =>
    isLaborEncounterAmendCompletionProvenanceForEncounterVersion(
      provenance,
      encounterQuestionnaireResponseId,
      encounterQuestionnaireResponseVersionId,
    ),
  );
}

function mapAmendmentReasonToProvenanceReason(
  amendmentReason: EncounterAmendmentReason | undefined,
): CodeableConcept[] | undefined {
  if (!amendmentReason) {
    return undefined;
  }

  const reasonCode = ENCOUNTER_AMENDMENT_REASON_TO_PROVENANCE_REASON_CODE[amendmentReason];
  if (!reasonCode) {
    return undefined;
  }

  return [
    {
      coding: [
        {
          system: Systems.HL7ActReason,
          code: reasonCode,
          display:
            EncounterAmendmentReasonLabels[
              amendmentReason as keyof typeof EncounterAmendmentReasonLabels
            ],
        },
      ],
    },
  ];
}

/**
 * Builds a Provenance resource for a labor encounter sign-off or amend-completion event.
 */
export function buildLaborEncounterEventProvenance({
  activityCode,
  encounterQuestionnaireResponseVersionId,
  targets,
  practitionerReference,
  amendmentReason,
}: BuildLaborEncounterEventProvenanceParams): Provenance {
  const recorded = new Date().toISOString();
  const reason =
    activityCode === LaborEncounterProvenanceActivityCode.AmendCompletion
      ? mapAmendmentReasonToProvenanceReason(amendmentReason)
      : undefined;

  return {
    resourceType: "Provenance",
    target: targets,
    recorded,
    occurredDateTime: recorded,
    reason,
    activity: {
      coding: [
        {
          system: LABOR_ENCOUNTER_PROVENANCE_ACTIVITY_SYSTEM,
          code: activityCode,
          display: LABOR_ENCOUNTER_PROVENANCE_ACTIVITY_DISPLAY[activityCode],
        },
      ],
    },
    agent: [
      {
        type: {
          coding: [
            {
              system: Systems.HL7ProvenanceParticipantType,
              code: ProvenanceParticipantType.Performer,
              display: "Performer",
            },
          ],
        },
        who: practitionerReference,
      },
    ],
    extension: setExtensionValue(
      undefined,
      Extensions.ProvenanceEncounterQuestionnaireResponseVersionId,
      { valueString: encounterQuestionnaireResponseVersionId },
    ),
  };
}

/**
 * Reads the encounter QuestionnaireResponse version id stored on a labor event Provenance.
 */
export function getProvenanceEncounterQuestionnaireResponseVersionId(
  provenance: Provenance | undefined,
): string | undefined {
  if (!provenance) {
    return undefined;
  }

  const fromExtension = getExtensionValue(
    provenance,
    Extensions.ProvenanceEncounterQuestionnaireResponseVersionId,
  );
  return typeof fromExtension === "string" && fromExtension.length > 0 ? fromExtension : undefined;
}

/**
 * Parses the encounter amendment reason from amend form values for Provenance.reason.
 */
export function getEncounterAmendmentReasonFromFormValues(
  formValues: EncounterAmendmentFormType,
): EncounterAmendmentReason | undefined {
  const reason = formValues.encounterAmendmentInfo?.encounterAmendmentInfoReason;
  if (typeof reason === "string") {
    return reason as EncounterAmendmentReason;
  }
  if (reason && typeof reason === "object" && "code" in reason && typeof reason.code === "string") {
    return reason.code as EncounterAmendmentReason;
  }
  return undefined;
}

/**
 * Returns whether an existing Provenance already records this encounter QuestionnaireResponse version event.
 */
export function isLaborEncounterEventProvenanceForVersion(
  provenance: Provenance,
  encounterQuestionnaireResponseVersionId: string,
): boolean {
  return (
    getProvenanceEncounterQuestionnaireResponseVersionId(provenance) ===
    encounterQuestionnaireResponseVersionId
  );
}

/**
 * Finds labor event Provenance for an encounter QuestionnaireResponse version id.
 */
export function findLaborEncounterEventProvenanceForVersion(
  provenances: ReadonlyArray<Provenance>,
  encounterQuestionnaireResponseVersionId: string,
): Provenance | undefined {
  return provenances.find((provenance) =>
    isLaborEncounterEventProvenanceForVersion(provenance, encounterQuestionnaireResponseVersionId),
  );
}
